import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { FinetunePanel } from "./FinetunePanel";
import { Button, ErrorNote } from "./primitives";
import { api } from "../lib/api";
import type { FinetuneTarget, RunEvent } from "../lib/types";

/** What the app is actually wearing: the stamp if there is one, else the OS. */
function currentTheme(): "light" | "dark" {
  const stamped = document.documentElement.getAttribute("data-theme");
  if (stamped === "light" || stamped === "dark") return stamped;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

/**
 * The HTML deliverable, in a sandboxed frame.
 *
 * `allow-scripts` without `allow-same-origin` is the deliberate pair: the
 * charts need to run, and the frame gets an opaque origin so nothing in it can
 * reach the app, its storage, or its cookies. The server also sends a
 * content-security policy permitting no outbound request, so a deliverable
 * that quietly depends on the network fails here rather than in a client's
 * inbox.
 *
 * **The frame follows the app's theme rather than the machine's.** The page
 * decides for itself everywhere else — that is what makes it work emailed or
 * printed — but here a reader who has put the app in light mode on a dark
 * laptop would otherwise get a dark document beside a light page. An opaque
 * origin cannot be reached into, so the theme is posted and `reader.js` stamps
 * it.
 *
 * Sent on three occasions, because any one of them alone loses the race: when
 * the frame reports its listener is live, when the frame finishes loading, and
 * whenever the app's theme changes afterwards.
 *
 * **A document rendered before this existed cannot follow, and says so.** The
 * reading shell is inlined into each file when it is built, so a deliverable
 * on disk carries whatever `reader.js` looked like the day its run finished —
 * and an old one simply ignores the message. That is invisible from here: it
 * looks like a theme bug rather than an old file, which is exactly how it was
 * reported. The `ca-ready` handshake is the tell, and its absence is worth one
 * line rather than leaving somebody to guess.
 *
 * **A part of the document can be finetuned from here.** The frame cannot
 * reach the app and the app cannot reach into it, so the two talk the way the
 * theme does: once the frame says it can (`ca-ready` with `finetune`), the
 * app switches its hover layer on, and a reader pressing *Finetune* on a
 * heading or a figure is a `ca-finetune` message naming the part. The panel
 * opens beside the frame and the frame hides its evidence rail, so the panel
 * takes the rail's place. Only messages from this frame are listened to.
 *
 * **"Open in new tab" opens the app's reading page**, which is this component
 * at full height: a file opened on its own has no parent to send a request
 * through, and serving it with a policy that let it call the API would open
 * that to everything the file holds. Download is the file itself.
 */
export function HtmlPreview({
  runId,
  name,
  events = [],
  full = false,
}: {
  runId: string;
  name: string;
  /** The run's events, for the finetune panel's account of the work. */
  events?: RunEvent[];
  /** The reading page: the frame takes the window's height. */
  full?: boolean;
}) {
  const src = api.fileUrl(runId, name);
  const frame = useRef<HTMLIFrameElement>(null);
  // null while unknown — the note must not flash before the frame has had a
  // chance to answer.
  const [linked, setLinked] = useState<boolean | null>(null);
  //     Whether this document can be finetuned: a file rendered before the
  //     layer existed answers `ca-ready` without saying so.
  const [tunable, setTunable] = useState<boolean | null>(null);
  const [target, setTarget] = useState<FinetuneTarget | null>(null);
  //     Bumped to load the document again once a finetune has rendered it.
  const [version, setVersion] = useState(0);
  const latest = useRef<FinetuneTarget | null>(null);
  latest.current = target;
  const queryClient = useQueryClient();

  const post = useCallback((message: Record<string, unknown>) => {
    frame.current?.contentWindow?.postMessage(message, "*");
  }, []);

  const rerender = useMutation({
    mutationFn: () => api.republish(runId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["artefacts", runId] });
      setVersion((n) => n + 1);
    },
  });

  const close = useCallback(() => {
    setTarget(null);
    post({ type: "ca-finetune-panel", open: false });
    post({ type: "ca-finetune-mark", target: null });
  }, [post]);

  const send = useCallback(() => {
    // Read afresh rather than from state: this runs on the frame announcing
    // itself, which can happen before React has processed the change that
    // prompted it.
    const now = currentTheme();
    frame.current?.contentWindow?.postMessage(
      { type: "ca-theme", theme: now },
      "*",
    );
  }, []);

  useEffect(() => {
    // The frame announces itself once its listener exists. Without this, a
    // theme posted while the document was still parsing simply vanishes.
    const onMessage = (event: MessageEvent) => {
      // Only this frame's: anything else on the page could otherwise name a
      // part to finetune.
      if (event.source !== frame.current?.contentWindow) return;
      if (event.data?.type === "ca-ready") {
        setLinked(true);
        send();
        const can = event.data.finetune === true;
        setTunable(can);
        if (can) {
          post({ type: "ca-finetune-on" });
          // A document loaded again after a finetune: the panel is still
          // open, so the rail stays hidden and the part is shown once more.
          if (latest.current) {
            post({ type: "ca-finetune-panel", open: true });
            post({ type: "ca-finetune-mark", target: latest.current, scroll: true });
          }
        }
      }
      if (event.data?.type === "ca-finetune") {
        const picked = event.data.target as FinetuneTarget | undefined;
        if (
          !picked ||
          (picked.kind !== "section" && picked.kind !== "figure") ||
          typeof picked.line !== "number" ||
          Number.isNaN(picked.line)
        )
          return;
        setTarget(picked);
        post({ type: "ca-finetune-panel", open: true });
      }
    };
    window.addEventListener("message", onMessage);

    // Follow the app afterwards: the toggle stamps `data-theme` on the root,
    // and "system" removes it, which is why the OS query is watched too.
    const observer = new MutationObserver(send);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    const query = window.matchMedia?.("(prefers-color-scheme: dark)");
    query?.addEventListener("change", send);

    return () => {
      window.removeEventListener("message", onMessage);
      observer.disconnect();
      query?.removeEventListener("change", send);
    };
  }, [send, post]);

  // A fresh document answers during parse, so by the time `load` has fired and
  // a moment has passed, silence means the file predates the handshake.
  //
  // The timer is held and cleared, not fired and forgotten: the tab switches
  // between deliverables without remounting this component, so an old
  // document's pending timer would otherwise land on the next one and flag a
  // file that is perfectly fine.
  const pending = useRef<number | undefined>(undefined);

  useEffect(() => {
    setLinked(null);
    setTunable(null);
    return () => window.clearTimeout(pending.current);
  }, [src, version]);

  // Another document, another set of parts.
  useEffect(() => setTarget(null), [src]);

  const onLoad = useCallback(() => {
    send();
    window.clearTimeout(pending.current);
    pending.current = window.setTimeout(
      () => setLinked((known) => known ?? false),
      1200,
    );
  }, [send]);

  //     Both notes are answered by the same free re-render, so both offer it.
  const old = linked === false || (linked === true && tunable === false);
  const height = full ? "h-[calc(100dvh-7.5rem)]" : "h-[calc(100vh-16rem)] min-h-[32rem]";

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-end gap-3 border-b border-line px-4 py-2 text-xs">
        {tunable && !target && (
          <span className="mr-auto text-muted">
            Point at a heading or a figure to finetune it.
          </span>
        )}
        {!full && (
          <a
            href={api.readerUrl(runId, name)}
            target="_blank"
            rel="noreferrer"
            className="text-accent"
          >
            Open in new tab
          </a>
        )}
        <a href={api.fileUrl(runId, name, true)} className="text-accent">
          Download
        </a>
      </div>
      {old && (
        <div className="flex flex-wrap items-center gap-3 border-b border-line bg-surface-2 px-4 py-2 text-xs text-muted">
          <p className="min-w-0 flex-1">
            {linked === false
              ? "This deliverable was rendered before the preview could pass the theme in, so it follows your operating system rather than the app, and its parts cannot be finetuned."
              : "This deliverable was rendered before its parts could be finetuned."}{" "}
            Rendering it again from the markdown it already has costs nothing and changes no
            content.
          </p>
          <Button size="sm" onClick={() => rerender.mutate()} disabled={rerender.isPending}>
            {rerender.isPending ? "Rendering…" : "Render it again"}
          </Button>
        </div>
      )}
      {rerender.isError && (
        <div className="border-b border-line px-4 py-2">
          <ErrorNote error={rerender.error} />
        </div>
      )}
      <div className={target ? `grid lg:grid-cols-[minmax(0,1fr)_24rem]` : ""}>
        <iframe
          key={`${src}#${version}`}
          ref={frame}
          src={src}
          title={name}
          onLoad={onLoad}
          sandbox="allow-scripts"
          className={`${height} w-full border-0 bg-surface`}
        />
        {target && (
          <div className={`${height} min-h-0`}>
            <FinetunePanel
              runId={runId}
              name={name}
              target={target}
              events={events}
              onClose={close}
              onLanded={() => setVersion((n) => n + 1)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
